export const CHUNK_RELOAD_STORAGE_KEY = "b3os:chunk-reload-at";
export const CHUNK_RELOAD_COOLDOWN_MS = 10_000;

const CHUNK_MESSAGE_RE =
  /Loading chunk .* failed|Failed to load chunk|ChunkLoadError|Importing a module script failed|Unable to load script/i;

export function isChunkLoadError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  if ("name" in err && err.name === "ChunkLoadError") return true;
  if ("message" in err && typeof err.message === "string" && CHUNK_MESSAGE_RE.test(err.message)) return true;
  return false;
}

function remainingCooldownMs(raw: string | null): number {
  if (raw === null) return 0;
  const last = Number.parseInt(raw, 10);
  const now = Date.now();
  // Reject NaN and future-looking timestamps — a corrupted storage value in
  // the future would otherwise produce a negative `elapsed` and keep the
  // cooldown active indefinitely.
  if (!Number.isFinite(last) || last > now) return 0;
  return CHUNK_RELOAD_COOLDOWN_MS - (now - last);
}

/**
 * Read-only check — safe to call from render. Returns true while a recent
 * reload is still inside the cooldown window, so callers can decide to show
 * the error UI instead of relying on a fallback from the effect body.
 */
export function isChunkReloadOnCooldown(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return remainingCooldownMs(sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY)) > 0;
  } catch {
    return false;
  }
}

export function shouldReloadForChunkError(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (remainingCooldownMs(sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY)) > 0) return false;
    sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(Date.now()));
    return true;
  } catch {
    // Storage blocked (sandboxed iframe, Safari private mode under pressure, etc.).
    // Fall through and allow the reload — worst case is one extra reload.
    return true;
  }
}

export function safeReload(reason: string): boolean {
  if (!shouldReloadForChunkError()) return false;
  console.warn(`[chunk-error-recovery] reloading: ${reason}`);
  window.location.reload();
  return true;
}
